import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SectionCard, InfoNote } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput } from "@/components/common/Inputs";
import { newIspPackage } from "@/lib/engines/isp";
import { fmtNum, fmtPct, fmtRp } from "@/lib/format";

export default function IspPackagesPanel({ data, setData, setPackages, result, readOnly }) {
  const isNew = data.region.status === "new";
  const add = () => setPackages((p) => [...p, newIspPackage()]);
  const remove = (id) => setPackages((p) => p.filter((x) => x.id !== id));
  const update = (id, patch) => setPackages((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  return (
    <div className="space-y-5">
      <SectionCard
        title={`Paket Internet (${data.packages.length})`}
        eyebrow={isNew ? "Wilayah Baru · mix %" : "Wilayah Existing · jumlah pelanggan nyata"}
        description={isNew ? "Masukkan mix (%) paket untuk membagi target pelanggan." : "Masukkan jumlah pelanggan aktual untuk setiap paket."}
        actions={
          <Button onClick={add} disabled={readOnly} size="sm" data-testid="isp-packages-add">
            <Plus className="h-4 w-4" /> Tambah paket
          </Button>
        }
        bodyClassName="p-0"
        testid="isp-packages-card"
      >
        <div className="overflow-x-auto scroll-gold">
          <table className="dense-table w-full text-xs" data-testid="isp-packages-table">
            <thead>
              <tr className="bg-secondary">
                <th className="text-left px-4 py-3">Paket</th>
                <th className="text-right px-3 py-3 w-[130px]">Kecepatan</th>
                <th className="text-right px-3 py-3 w-[150px]">Harga</th>
                <th className="text-right px-3 py-3 w-[110px]">Kontensi</th>
                <th className="text-right px-3 py-3 w-[130px]">{isNew ? "Mix %" : "Pelanggan"}</th>
                <th className="text-right px-3 py-3 w-[130px]">Pelanggan efektif</th>
                <th className="text-right px-3 py-3 w-[130px]">COGS/bln</th>
                <th className="text-right px-3 py-3 w-[130px]">Margin</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {data.packages.map((p) => {
                const r = result.rows.find((x) => x.id === p.id);
                return (
                  <tr key={p.id} className="border-t border-border hover:bg-[hsl(220_10%_9%)]" data-testid={`isp-package-row-${p.id}`}>
                    <td className="px-4 py-2">
                      <Input value={p.name} onChange={(e) => update(p.id, { name: e.target.value })} className="h-9 bg-[hsl(220_12%_8%)] font-medium" disabled={readOnly} data-testid={`isp-package-name-${p.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <NumInput value={p.speedMbps} onChange={(v) => update(p.id, { speedMbps: v })} suffix="Mbps" decimals={0} disabled={readOnly} testid={`isp-package-speed-${p.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <MoneyInput value={p.price} onChange={(v) => update(p.id, { price: v })} disabled={readOnly} testid={`isp-package-price-${p.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <NumInput value={p.contention} onChange={(v) => update(p.id, { contention: v })} suffix=":1" decimals={0} disabled={readOnly} testid={`isp-package-contention-${p.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      {isNew ? (
                        <NumInput value={p.mixPct} onChange={(v) => update(p.id, { mixPct: v })} suffix="%" decimals={0} disabled={readOnly} testid={`isp-package-mix-${p.id}`} />
                      ) : (
                        <NumInput value={p.subscribers} onChange={(v) => update(p.id, { subscribers: v })} decimals={0} disabled={readOnly} testid={`isp-package-subs-${p.id}`} />
                      )}
                    </td>
                    <td className="px-3 py-2 text-right num" data-testid={`isp-package-effective-subs-${p.id}`}>{r ? fmtNum(r.subs) : "–"}</td>
                    <td className="px-3 py-2 text-right num" data-testid={`isp-package-cogs-${p.id}`}>{r ? fmtRp(r.cogs) : "–"}</td>
                    <td className="px-3 py-2 text-right">
                      {r ? (
                        <Badge variant="outline" className={`border-border text-[10px] num ${r.marginPct >= 25 ? "text-[hsl(160_55%_60%)]" : r.marginPct >= 10 ? "text-[hsl(43_60%_66%)]" : "text-[hsl(0_72%_64%)]"}`}>
                          {fmtPct(r.marginPct)}
                        </Badge>
                      ) : "–"}
                    </td>
                    <td className="px-2 py-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(0_72%_62%)]" onClick={() => remove(p.id)} disabled={readOnly} data-testid={`isp-package-remove-${p.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {data.packages.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Belum ada paket.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-secondary border-t border-border">
                <td colSpan={5} className="px-4 py-3 font-semibold">Total</td>
                <td className="px-3 py-3 text-right num font-semibold" data-testid="isp-packages-total-subs">{fmtNum(result.totalSubs)}</td>
                <td className="px-3 py-3 text-right num font-semibold">{fmtRp(result.totalCost / Math.max(1, result.totalSubs))}</td>
                <td className="px-3 py-3 text-right num font-semibold text-primary">{fmtPct(result.marginPct)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </SectionCard>

      <InfoNote>
        Kontensi {`<=`} 8:1 adalah rasio umum untuk FTTH residential. {`<=`} 4:1 untuk kelas premium. Pakai nilai tinggi (16:1+) hanya untuk paket entry-level.
      </InfoNote>
    </div>
  );
}
